import {memo, RefObject, useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, cx} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {useElementWasVisible} from "@pg-mono/hooks";

import {plansUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {ApplicationSource} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {DetailConstructionGallerySection} from "../../../atoms/DetailConstructionGallerySection";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {useShouldShowPrice} from "../../../hooks/use_should_show_price";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../../hotline/constants/hotline_chatbot_modal_trigger";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {useTransportPois} from "../../../maps/hooks/use_transport_pois";
import {IPoi} from "../../../maps/types/IPoi";
import {TransportPoiType} from "../../../maps/utils/PoiType";
import {initializePlansScript, PLANS_SCRIPT_ID} from "../../../plans/initialize_plans_script";
import {Promotions} from "../../../promotion/components/Promotions";
import {OSM_MAP_CONTAINER} from "../../../property/components/PropertyDetailForSale";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {Country} from "../../../region/types/Country";
import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {TagType} from "../../../types/TagType";
import {ViewType} from "../../../view_type/ViewType";
import {OfferNavBar} from "../../atoms/OfferNavBar";
import {OfferSalesOffices} from "../../atoms/OfferSalesOffices";
import {OfferUVP} from "../../atoms/OfferUVP";
import {DetailNearbyOffers} from "../../components/DetailNearbyOffers";
import {OfferDetailOpinions} from "../../components/opinions/OfferDetailOpinions";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {getOfferTypeNamePlural, getOfferTypeNamePluralCapital} from "../../helpers/OfferType";
import {hitGTMOfferViewItem} from "../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {IOfferDetailPromotion} from "../actions/fetch_offer_detail_promotions";
import {PropertyListTabTypes} from "../actions/fetch_offer_detail_properties";
import {OFFER_CHAT_BOT_TRIGGER} from "../constants/chat_bot_trigger";
import {OfferDetailPreSaleForm} from "../OfferDetailPreSaleForm";
import {OfferDetailPropertyList} from "./property_list/OfferDetailPropertyList";
import {OfferDetailStages} from "./stages/OfferDetailStages";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailCurrentInfo} from "./OfferDetailCurrentInfo";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {OfferDetailDetails} from "./OfferDetailDetails";
import {OfferDetailGetHomeLink} from "./OfferDetailGetHomeLink";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferDetailLocationMap} from "./OfferDetailLocationMap";
import {OfferDetailLocationSummary} from "./OfferDetailLocationSummary";
import {OfferVideo} from "./OfferVideo";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[];
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    refElems: {
        refGallery: RefObject<HTMLTableSectionElement | null>;
        refProperties: RefObject<HTMLTableSectionElement | null>;
        refDescription: RefObject<HTMLTableSectionElement | null>;
        refPromotions: RefObject<HTMLTableSectionElement | null>;
        refLocation: RefObject<HTMLTableSectionElement | null>;
        refContact: RefObject<HTMLTableSectionElement | null>;
    };
    visibleElement: string | null;
    changeVisibility: (name: string) => void;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    hasDescription: boolean;
    hasProperties: boolean;
    shouldMapBeRendered?: boolean;
}

interface IValueLabel {
    value: number;
    label: string;
}

export const OfferDetailForSale = memo((props: IProps) => {
    const {offer, promotions, variables, refElems, visibleElement, changeModalTab, hasDescription, hasProperties, changeVisibility, shouldMapBeRendered} =
        props;

    const [currentFilters, setCurrentFilter] = useState<ISearchFiltersFormValues | null>(null);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const rankingItem = getRankingItem(offer.id);

    /**
     * Plans
     */

    const [isPlanScriptLoading, setIsPlanScriptLoading] = useState(false);
    const onPlansOpenClick = async () => {
        if (!plansUrl) {
            console.error("plansUrl is not defined");
            return;
        }

        if (!viewType) {
            console.error("viewType is not defined");
            return;
        }

        const source = 3;
        const {site, site_id} = getTrackedSiteData();

        setIsPlanScriptLoading(true);

        await initializePlansScript(
            offer.id,
            source,
            plansUrl,
            {
                view_type: viewType,
                site,
                site_id
            },
            {
                renderApplicationButton: () => {
                    return <span />;
                },
                renderFavouriteButton: () => {
                    return <span />;
                }
            }
        );

        setIsPlanScriptLoading(false);
    };

    /**
     * Rest
     */

    const hasPromotions = promotions.length > 0;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const scrollToRef = (ref: React.RefObject<HTMLTableSectionElement | null>) =>
        ref.current && ref.current.scrollIntoView({behavior: "smooth", block: "start", inline: "center"});

    const scrollTo = useCallback(
        (_: number, refElem: React.RefObject<HTMLTableSectionElement | null>) => (e: React.MouseEvent) => {
            e.preventDefault();
            scrollToRef(refElem);
        },
        [visibleElement]
    );

    const location = useLocation();

    const contactBarProps = {
        trackOffer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        trackVendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        }
    };

    const propertyListObj = usePropertyList(offer.id, PropertyListTabTypes.FILTER, refElems.refProperties);

    const isPreSale = offer.configuration.pre_sale;

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = isOfferForSale(offer) && shouldShowPrice;
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);
    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );
    const standardType = !!variables?.offer_standard_description_type ? variables.offer_standard_description_type : "Informacja u dewelopera";

    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    const transportPoisFromApi = (transportPoiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });

    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05,
        disabled: !shouldMapBeRendered
    });

    const gtmMeta = {
        listName: `${getOfferTypeNamePluralCapital(offer.type)} w inwestycji`,
        listId: `${getOfferTypeNamePlural(offer.type)}_w_inwestycji`
    };

    useEffect(() => {
        hitGTMOfferViewItem({
            offer: offer
        });
    }, [offer.id]);

    return (
        <>
            <OfferDetailStages offer={offer} />

            <OfferNavBar
                className={offerNavBarWrapperStyle}
                refElements={refElems}
                offer={offer}
                promotions={promotions}
                onButtonClick={changeModalTab}
                // plans
                isPlanScriptLoading={isPlanScriptLoading}
                onPlansOpenClick={onPlansOpenClick}
            />

            <div id={PLANS_SCRIPT_ID} />

            <OfferUVP offer={offer} standardType={standardType} />

            <Container>
                {(hasProperties || isPreSale) && (
                    <section id="nieruchomosci" ref={refElems.refProperties} className={propertyListSectionStyles}>
                        {isPreSale ? (
                            <OfferDetailPreSaleForm offer={offer} />
                        ) : (
                            <OfferDetailPropertyList
                                offer={offer}
                                offerId={offer.id}
                                onFilterValuesChange={setCurrentFilter}
                                propertyListObj={propertyListObj}
                                propertyListType={PropertyListType.TILES}
                                gtmMeta={gtmMeta}
                            />
                        )}
                    </section>
                )}

                {/* Currently GetHome has only offers from Poland */}
                {offer.region.country === Country.POLAND && (
                    <section>
                        <OfferDetailGetHomeLink
                            filterData={currentFilters}
                            offer={offer}
                            region={offer.region.name}
                            regionId={offer.region.id}
                            stats={offer.region.stats}
                        />
                    </section>
                )}

                <Container>
                    {hasPromotions && (
                        <section id="promocje" ref={refElems.refPromotions} className={promotionsStyle}>
                            <Promotions
                                promotions={promotions}
                                vendorId={offer.vendor.id}
                                offerId={offer.id}
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                isSaleOverLimit={getOfferDisplayStatus(offer) === "limited"}
                            />
                        </section>
                    )}
                </Container>

                <section className={opinionsWrapper}>
                    <OfferDetailOpinions offer={offer} />
                </section>
            </Container>

            {shouldMapBeRendered ? (
                <section id="okolica-mapa" className={cx(css({mt: 4, mb: 2}), mapSectionContainer)} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded && (
                        <OfferDetailLocationMap
                            offer={offer}
                            onFullscreenClick={() => changeModalTab(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.ZOOM_MAP)}
                        />
                    )}
                </section>
            ) : null}

            <Container>
                <div className={cx(css({mb: 3, px: 1.5, md: {px: 3}}))}>
                    <OfferDetailLocationSummary
                        changeModalTab={changeModalTab}
                        offer={offer}
                        geoAreaCoordinates={offer.geo_area.coordinates}
                        geoPointCoordinates={offer.geo_point.coordinates}
                        routes={routes}
                    />
                </div>

                <div id={OFFER_CHAT_BOT_TRIGGER}>
                    <section className={detailConstructionSectionStyle}>
                        <DetailConstructionGallerySection
                            onButtonClick={() => changeModalTab(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BANNER)}
                            type="offer"
                        />
                    </section>

                    {groupedOfferAmenities.length > 0 && (
                        <section className={offerDetailAmenitiesSection}>
                            <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                        </section>
                    )}
                </div>

                <section className={detailsSectionMargin}>
                    <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                </section>

                <section className={detailsSectionMargin}>
                    <OfferDetailCurrentInfo
                        offer={offer}
                        variables={variables}
                        showPrice={showPrice}
                        changeVisibility={(e) => {
                            changeVisibility("properties");
                            location.hash = "nieruchomosci";
                            propertyListObj.onTabChange(PropertyListTabTypes.INVESTMENT, ViewType.OFFER);
                            scrollTo(300, refElems.refProperties)(e);
                        }}
                    />
                </section>

                {rankingItem && (
                    <section id="ranking" className={detailsSectionMargin}>
                        <OfferRankingModule rankingItem={rankingItem} />
                    </section>
                )}

                {offer.video && (
                    <section className={detailsSectionMargin}>
                        <OfferVideo url={offer.video} />
                    </section>
                )}

                {/*trigger for chatbot hotline modal*/}
                <div id={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} />

                {hasDescription && (
                    <section id="opis" ref={refElems.refDescription} className={detailsSectionMargin}>
                        <OfferDetailDescription offer={offer} />
                    </section>
                )}

                {offer.region.country === Country.POLAND && (
                    <section id="okolica" ref={refElems.refLocation}>
                        {transportPoiData && transportPoiData.poi && (
                            <OfferDetailLocation
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                                changeModalTab={changeModalTab}
                                pois={transportPoiData.poi.pois}
                            />
                        )}
                    </section>
                )}

                <section id="timestamp" className={timeStampSectionStyle}>
                    <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                </section>

                <section id="kontakt" ref={refElems.refContact} className={contactSectionStyle}>
                    <OfferSalesOffices offer={offer} />
                </section>
            </Container>

            <DetailNearbyOffers className={nearbyOffersHolder} viewType={ViewType.OFFER} offer={offer} />

            {isOfferForSale(offer) && (
                <ContactBar
                    salesOffices={offer.sales_offices}
                    vendor={offer.vendor}
                    trackVendor={contactBarProps.trackVendor}
                    trackOffer={contactBarProps.trackOffer}
                    phoneClip={offer.configuration.phone_clip}
                    isPhoneClipEnabled={offer.is_phone_clip_enabled}
                    source={ApplicationSource.Offer}
                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                    offer={offer}
                />
            )}
        </>
    );
});

const propertyListSectionStyles = css({
    position: "relative",
    zIndex: 1,
    mb: 3,

    md: {
        mb: 3
    }
});

const contactSectionStyle = css({
    mb: 2
});

const nearbyOffersHolder = css({
    mb: 5,

    md: {
        mb: 10
    }
});

const offerNavBarWrapperStyle = css({
    m: "0 0 0.3rem 0",
    display: "none",

    md: {
        mb: 3,
        display: "block"
    }
});

const promotionsStyle = css({
    mb: 2,

    md: {
        m: "0 0 6rem 0",
        mb: 7
    }
});

const detailsSectionMargin = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const opinionsWrapper = css({
    mt: 2,

    md: {
        width: "calc(288px * 3 + 1.6rem * 2)",
        mt: 3,
        mx: "auto"
    }
});

const timeStampSectionStyle = css({
    mb: 1,

    md: {
        mb: 3
    }
});

const detailConstructionSectionStyle = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const offerDetailAmenitiesSection = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const mapSectionContainer = css({
    minHeight: "58rem",

    md: {
        minHeight: "55rem"
    },

    lg: {
        minHeight: "68rem"
    }
});
import {memo, RefObject, useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, cx} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {useElementWasVisible} from "@pg-mono/hooks";

import {plansUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {ApplicationSource} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {DetailConstructionGallerySection} from "../../../atoms/DetailConstructionGallerySection";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {useShouldShowPrice} from "../../../hooks/use_should_show_price";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../../hotline/constants/hotline_chatbot_modal_trigger";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {useTransportPois} from "../../../maps/hooks/use_transport_pois";
import {IPoi} from "../../../maps/types/IPoi";
import {TransportPoiType} from "../../../maps/utils/PoiType";
import {initializePlansScript, PLANS_SCRIPT_ID} from "../../../plans/initialize_plans_script";
import {Promotions} from "../../../promotion/components/Promotions";
import {OSM_MAP_CONTAINER} from "../../../property/components/PropertyDetailForSale";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {Country} from "../../../region/types/Country";
import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {TagType} from "../../../types/TagType";
import {ViewType} from "../../../view_type/ViewType";
import {OfferNavBar} from "../../atoms/OfferNavBar";
import {OfferSalesOffices} from "../../atoms/OfferSalesOffices";
import {OfferUVP} from "../../atoms/OfferUVP";
import {DetailNearbyOffers} from "../../components/DetailNearbyOffers";
import {OfferDetailOpinions} from "../../components/opinions/OfferDetailOpinions";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {getOfferTypeNamePlural, getOfferTypeNamePluralCapital} from "../../helpers/OfferType";
import {hitGTMOfferViewItem} from "../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {IOfferDetailPromotion} from "../actions/fetch_offer_detail_promotions";
import {PropertyListTabTypes} from "../actions/fetch_offer_detail_properties";
import {OFFER_CHAT_BOT_TRIGGER} from "../constants/chat_bot_trigger";
import {OfferDetailPreSaleForm} from "../OfferDetailPreSaleForm";
import {OfferDetailPropertyList} from "./property_list/OfferDetailPropertyList";
import {OfferDetailStages} from "./stages/OfferDetailStages";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailCurrentInfo} from "./OfferDetailCurrentInfo";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {OfferDetailDetails} from "./OfferDetailDetails";
import {OfferDetailGetHomeLink} from "./OfferDetailGetHomeLink";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferDetailLocationMap} from "./OfferDetailLocationMap";
import {OfferDetailLocationSummary} from "./OfferDetailLocationSummary";
import {OfferVideo} from "./OfferVideo";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[];
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    refElems: {
        refGallery: RefObject<HTMLTableSectionElement | null>;
        refProperties: RefObject<HTMLTableSectionElement | null>;
        refDescription: RefObject<HTMLTableSectionElement | null>;
        refPromotions: RefObject<HTMLTableSectionElement | null>;
        refLocation: RefObject<HTMLTableSectionElement | null>;
        refContact: RefObject<HTMLTableSectionElement | null>;
    };
    visibleElement: string | null;
    changeVisibility: (name: string) => void;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    hasDescription: boolean;
    hasProperties: boolean;
    shouldMapBeRendered?: boolean;
}

interface IValueLabel {
    value: number;
    label: string;
}

export const OfferDetailForSale = memo((props: IProps) => {
    const {offer, promotions, variables, refElems, visibleElement, changeModalTab, hasDescription, hasProperties, changeVisibility, shouldMapBeRendered} =
        props;

    const [currentFilters, setCurrentFilter] = useState<ISearchFiltersFormValues | null>(null);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const rankingItem = getRankingItem(offer.id);

    /**
     * Plans
     */

    const [isPlanScriptLoading, setIsPlanScriptLoading] = useState(false);
    const onPlansOpenClick = async () => {
        if (!plansUrl) {
            console.error("plansUrl is not defined");
            return;
        }

        if (!viewType) {
            console.error("viewType is not defined");
            return;
        }

        const source = 3;
        const {site, site_id} = getTrackedSiteData();

        setIsPlanScriptLoading(true);

        await initializePlansScript(
            offer.id,
            source,
            plansUrl,
            {
                view_type: viewType,
                site,
                site_id
            },
            {
                renderApplicationButton: () => {
                    return <span />;
                },
                renderFavouriteButton: () => {
                    return <span />;
                }
            }
        );

        setIsPlanScriptLoading(false);
    };

    /**
     * Rest
     */

    const hasPromotions = promotions.length > 0;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const scrollToRef = (ref: React.RefObject<HTMLTableSectionElement | null>) =>
        ref.current && ref.current.scrollIntoView({behavior: "smooth", block: "start", inline: "center"});

    const scrollTo = useCallback(
        (_: number, refElem: React.RefObject<HTMLTableSectionElement | null>) => (e: React.MouseEvent) => {
            e.preventDefault();
            scrollToRef(refElem);
        },
        [visibleElement]
    );

    const location = useLocation();

    const contactBarProps = {
        trackOffer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        trackVendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        }
    };

    const propertyListObj = usePropertyList(offer.id, PropertyListTabTypes.FILTER, refElems.refProperties);

    const isPreSale = offer.configuration.pre_sale;

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = isOfferForSale(offer) && shouldShowPrice;
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);
    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );
    const standardType = !!variables?.offer_standard_description_type ? variables.offer_standard_description_type : "Informacja u dewelopera";

    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    const transportPoisFromApi = (transportPoiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });

    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05,
        disabled: !shouldMapBeRendered
    });

    const gtmMeta = {
        listName: `${getOfferTypeNamePluralCapital(offer.type)} w inwestycji`,
        listId: `${getOfferTypeNamePlural(offer.type)}_w_inwestycji`
    };

    useEffect(() => {
        hitGTMOfferViewItem({
            offer: offer
        });
    }, [offer.id]);

    return (
        <>
            <OfferDetailStages offer={offer} />

            <OfferNavBar
                className={offerNavBarWrapperStyle}
                refElements={refElems}
                offer={offer}
                promotions={promotions}
                onButtonClick={changeModalTab}
                // plans
                isPlanScriptLoading={isPlanScriptLoading}
                onPlansOpenClick={onPlansOpenClick}
            />

            <div id={PLANS_SCRIPT_ID} />

            <OfferUVP offer={offer} standardType={standardType} />

            <Container>
                {(hasProperties || isPreSale) && (
                    <section id="nieruchomosci" ref={refElems.refProperties} className={propertyListSectionStyles}>
                        {isPreSale ? (
                            <OfferDetailPreSaleForm offer={offer} />
                        ) : (
                            <OfferDetailPropertyList
                                offer={offer}
                                offerId={offer.id}
                                onFilterValuesChange={setCurrentFilter}
                                propertyListObj={propertyListObj}
                                propertyListType={PropertyListType.TILES}
                                gtmMeta={gtmMeta}
                            />
                        )}
                    </section>
                )}

                {/* Currently GetHome has only offers from Poland */}
                {offer.region.country === Country.POLAND && (
                    <section>
                        <OfferDetailGetHomeLink
                            filterData={currentFilters}
                            offer={offer}
                            region={offer.region.name}
                            regionId={offer.region.id}
                            stats={offer.region.stats}
                        />
                    </section>
                )}

                <Container>
                    {hasPromotions && (
                        <section id="promocje" ref={refElems.refPromotions} className={promotionsStyle}>
                            <Promotions
                                promotions={promotions}
                                vendorId={offer.vendor.id}
                                offerId={offer.id}
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                isSaleOverLimit={getOfferDisplayStatus(offer) === "limited"}
                            />
                        </section>
                    )}
                </Container>

                <section className={opinionsWrapper}>
                    <OfferDetailOpinions offer={offer} />
                </section>
            </Container>

            {shouldMapBeRendered ? (
                <section id="okolica-mapa" className={cx(css({mt: 4, mb: 2}), mapSectionContainer)} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded && (
                        <OfferDetailLocationMap
                            offer={offer}
                            onFullscreenClick={() => changeModalTab(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.ZOOM_MAP)}
                        />
                    )}
                </section>
            ) : null}

            <Container>
                <div className={cx(css({mb: 3, px: 1.5, md: {px: 3}}))}>
                    <OfferDetailLocationSummary
                        changeModalTab={changeModalTab}
                        offer={offer}
                        geoAreaCoordinates={offer.geo_area.coordinates}
                        geoPointCoordinates={offer.geo_point.coordinates}
                        routes={routes}
                    />
                </div>

                <div id={OFFER_CHAT_BOT_TRIGGER}>
                    <section className={detailConstructionSectionStyle}>
                        <DetailConstructionGallerySection
                            onButtonClick={() => changeModalTab(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BANNER)}
                            type="offer"
                        />
                    </section>

                    {groupedOfferAmenities.length > 0 && (
                        <section className={offerDetailAmenitiesSection}>
                            <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                        </section>
                    )}
                </div>

                <section className={detailsSectionMargin}>
                    <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                </section>

                <section className={detailsSectionMargin}>
                    <OfferDetailCurrentInfo
                        offer={offer}
                        variables={variables}
                        showPrice={showPrice}
                        changeVisibility={(e) => {
                            changeVisibility("properties");
                            location.hash = "nieruchomosci";
                            propertyListObj.onTabChange(PropertyListTabTypes.INVESTMENT, ViewType.OFFER);
                            scrollTo(300, refElems.refProperties)(e);
                        }}
                    />
                </section>

                {rankingItem && (
                    <section id="ranking" className={detailsSectionMargin}>
                        <OfferRankingModule rankingItem={rankingItem} />
                    </section>
                )}

                {offer.video && (
                    <section className={detailsSectionMargin}>
                        <OfferVideo url={offer.video} />
                    </section>
                )}

                {/*trigger for chatbot hotline modal*/}
                <div id={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} />

                {hasDescription && (
                    <section id="opis" ref={refElems.refDescription} className={detailsSectionMargin}>
                        <OfferDetailDescription offer={offer} />
                    </section>
                )}

                {offer.region.country === Country.POLAND && (
                    <section id="okolica" ref={refElems.refLocation}>
                        {transportPoiData && transportPoiData.poi && (
                            <OfferDetailLocation
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                                changeModalTab={changeModalTab}
                                pois={transportPoiData.poi.pois}
                            />
                        )}
                    </section>
                )}

                <section id="timestamp" className={timeStampSectionStyle}>
                    <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                </section>

                <section id="kontakt" ref={refElems.refContact} className={contactSectionStyle}>
                    <OfferSalesOffices offer={offer} />
                </section>
            </Container>

            <DetailNearbyOffers className={nearbyOffersHolder} viewType={ViewType.OFFER} offer={offer} />

            {isOfferForSale(offer) && (
                <ContactBar
                    salesOffices={offer.sales_offices}
                    vendor={offer.vendor}
                    trackVendor={contactBarProps.trackVendor}
                    trackOffer={contactBarProps.trackOffer}
                    phoneClip={offer.configuration.phone_clip}
                    isPhoneClipEnabled={offer.is_phone_clip_enabled}
                    source={ApplicationSource.Offer}
                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                    offer={offer}
                />
            )}
        </>
    );
});

const propertyListSectionStyles = css({
    position: "relative",
    zIndex: 1,
    mb: 3,

    md: {
        mb: 3
    }
});

const contactSectionStyle = css({
    mb: 2
});

const nearbyOffersHolder = css({
    mb: 5,

    md: {
        mb: 10
    }
});

const offerNavBarWrapperStyle = css({
    m: "0 0 0.3rem 0",
    display: "none",

    md: {
        mb: 3,
        display: "block"
    }
});

const promotionsStyle = css({
    mb: 2,

    md: {
        m: "0 0 6rem 0",
        mb: 7
    }
});

const detailsSectionMargin = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const opinionsWrapper = css({
    mt: 2,

    md: {
        width: "calc(288px * 3 + 1.6rem * 2)",
        mt: 3,
        mx: "auto"
    }
});

const timeStampSectionStyle = css({
    mb: 1,

    md: {
        mb: 3
    }
});

const detailConstructionSectionStyle = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const offerDetailAmenitiesSection = css({
    mb: 2,

    md: {
        mb: 3
    }
});

const mapSectionContainer = css({
    minHeight: "58rem",

    md: {
        minHeight: "55rem"
    },

    lg: {
        minHeight: "68rem"
    }
});
